import type { EnvironmentId, ProjectSearchEntriesResult } from "@cmmd-forge/contracts";
import { queryOptions } from "@tanstack/react-query";
import { ensureEnvironmentApi, readEnvironmentApi } from "~/environmentApi";
import { projectQueryKeys } from "./projectQueryKeys";

const DEFAULT_SEARCH_ENTRIES_LIMIT = 80;
const DEFAULT_SEARCH_ENTRIES_STALE_TIME = 15_000;
const DEFAULT_SEARCH_CONTENTS_LIMIT = 100;
const DEFAULT_SEARCH_CONTENTS_STALE_TIME = 5_000;
const EMPTY_SEARCH_ENTRIES_RESULT: ProjectSearchEntriesResult = {
  entries: [],
  truncated: false,
};

export function projectSearchContentsQueryOptions(input: {
  readonly environmentId: EnvironmentId | null;
  readonly cwd: string | null;
  readonly query: string;
  readonly caseSensitive: boolean;
  readonly wholeWord: boolean;
  readonly useRegex: boolean;
  readonly enabled?: boolean;
  readonly limit?: number;
  readonly staleTime?: number;
}) {
  const limit = input.limit ?? DEFAULT_SEARCH_CONTENTS_LIMIT;
  return queryOptions({
    queryKey: projectQueryKeys.searchContents(
      input.environmentId,
      input.cwd,
      input.query,
      limit,
      input.caseSensitive,
      input.wholeWord,
      input.useRegex,
    ),
    queryFn: async ({ signal }) => {
      if (!input.cwd || !input.environmentId) {
        throw new Error("Project content search is unavailable.");
      }
      const api = readEnvironmentApi(input.environmentId);
      if (!api) {
        throw new Error("Project content search requires a connected environment.");
      }
      const result = await api.projects.searchContents(
        {
          cwd: input.cwd,
          query: input.query,
          limit,
          caseSensitive: input.caseSensitive,
          wholeWord: input.wholeWord,
          useRegex: input.useRegex,
        },
        { signal },
      );
      if (signal.aborted) throw new DOMException("Search was cancelled.", "AbortError");
      return result;
    },
    enabled:
      (input.enabled ?? true) &&
      input.environmentId !== null &&
      input.cwd !== null &&
      input.query.length > 0,
    staleTime: input.staleTime ?? DEFAULT_SEARCH_CONTENTS_STALE_TIME,
  });
}

export function projectSearchEntriesQueryOptions(input: {
  environmentId: EnvironmentId | null;
  cwd: string | null;
  query: string;
  enabled?: boolean;
  limit?: number;
  staleTime?: number;
}) {
  const limit = input.limit ?? DEFAULT_SEARCH_ENTRIES_LIMIT;
  return queryOptions({
    queryKey: projectQueryKeys.searchEntries(input.environmentId, input.cwd, input.query, limit),
    queryFn: async () => {
      if (!input.cwd || !input.environmentId) {
        throw new Error("Project entry search is unavailable.");
      }
      const api = ensureEnvironmentApi(input.environmentId);
      return api.projects.searchEntries({
        cwd: input.cwd,
        query: input.query,
        limit,
      });
    },
    enabled:
      (input.enabled ?? true) &&
      input.environmentId !== null &&
      input.cwd !== null &&
      input.query.length > 0,
    staleTime: input.staleTime ?? DEFAULT_SEARCH_ENTRIES_STALE_TIME,
    placeholderData: (previous) => previous ?? EMPTY_SEARCH_ENTRIES_RESULT,
  });
}
